/**
 * FADING (craps) — non-shooter players cover the shooter's stack.
 * Mirror of cee-lo's FadingState; shooter-flavored copy.
 *
 * Mechanics identical: wallet opens for faders, stays closed for the
 * shooter who watches. Transitions out via server-driven
 * `bet_zone_filled` (stack fully covered) or `phase_timer_expired`.
 *
 * Craps wires `bankerId = shooterId` server-side, so `update.bankerId`
 * is the right field to use here too — keeping symmetry with the
 * cee-lo FadingState makes the migration one-to-one.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class CrapsFadingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;
    const shooterId = update.currentPlayerId ?? update.bankerId;
    const localId = game.getLocalPlayerId();
    const isLocalShooter = !!localId && shooterId === localId;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();

    // Wallet open for the FADERS (everyone except the shooter).
    await game.enterBettingMode({
      open: !isLocalShooter,
      animated: update.animated,
      previousState: update.previousState,
    });

    if (isLocalShooter) {
      game.instructionBanner?.hide();
    } else {
      game.instructionBanner?.show(
        {
          title: "Shooter's Stack",
          text: 'Fade the shooter. Drag bills onto the stack',
        },
        update.animated,
      );
    }

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(_update: GameUpdate): Promise<void> {
    // Wipe the BETTING activity pill — the next state may or may not
    // be another betting phase. SIDEBET_BETTING reuses the betting
    // camera + wallet on its own entry, no other teardown needed.
    this._clearBettingActivity();
  }
}
